/**
 * TalentMarketsCard — Iter-191
 *
 * Drop-in card that loads/saves a talent's selected metro markets.
 *
 * Props:
 *   variant        'talent' (self-service) | 'admin' (admin editing another talent)
 *   talentUserId   required when variant='admin'
 *   suggestCity    optional — auto-suggest hint passed to the picker
 *   suggestState   optional
 *   onSaved        optional callback after save
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { MapPin, Star, Pencil, Plus, Loader2 } from 'lucide-react';
import api from '../utils/api';
import MarketPickerModal from './MarketPickerModal';

export default function TalentMarketsCard({
  variant = 'talent',
  talentUserId = null,
  suggestCity = '',
  suggestState = '',
  onSaved,
}) {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);

  const fetchUrl =
    variant === 'admin' && talentUserId
      ? `/admin/users/${talentUserId}/markets`
      : '/talent/profile/markets';
  const saveUrl =
    variant === 'admin' && talentUserId
      ? `/admin/users/${talentUserId}/markets`
      : '/talent/profile/markets';

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(fetchUrl);
      setMarkets(Array.isArray(res.data) ? res.data : []);
    } catch {
      setMarkets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (variant === 'admin' && !talentUserId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, talentUserId]);

  const handleSave = async ({ market_ids, primary_market_id }) => {
    await api.put(saveUrl, { market_ids, primary_market_id });
    await load();
    if (onSaved) onSaved();
  };

  const primary = markets.find((m) => m.is_primary);
  const secondary = markets.filter((m) => !m.is_primary);

  return (
    <Card data-testid="talent-markets-card">
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="text-sm flex items-center gap-2">
            <MapPin className="w-4 h-4 text-cyan-500" /> Markets
          </CardTitle>
          <CardDescription className="text-xs">
            {variant === 'admin'
              ? 'Set the metro regions this talent works in.'
              : 'Tell us the metro regions you work in. Admins use this to invite you to local gigs.'}
          </CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPickerOpen(true)}
          className="border-cyan-300 text-cyan-700 hover:bg-cyan-50 h-8 text-xs flex-shrink-0"
          data-testid="open-market-picker-btn"
        >
          {markets.length === 0 ? (
            <><Plus className="w-3.5 h-3.5 mr-1" /> Add markets</>
          ) : (
            <><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</>
          )}
        </Button>
      </CardHeader>
      <CardContent className="pt-1">
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        ) : markets.length === 0 ? (
          <div className="text-xs text-gray-500 py-2">
            No markets selected yet. Add one to be discoverable in regional invite lists.
          </div>
        ) : (
          <div className="flex items-center gap-1.5 flex-wrap">
            {primary && (
              <Badge
                className="bg-amber-100 text-amber-800 border border-amber-200 text-[11px] font-medium"
                data-testid={`market-chip-primary-${primary.slug}`}
              >
                <Star className="w-3 h-3 mr-1 fill-amber-500 text-amber-500" />
                {primary.metro_name}
                <span className="ml-1 text-[9px] uppercase tracking-wide opacity-70">Primary</span>
              </Badge>
            )}
            {secondary.map((m) => (
              <Badge
                key={m.id}
                className="bg-white text-gray-700 border text-[11px] font-normal"
                data-testid={`market-chip-${m.slug}`}
              >
                {m.metro_name}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>

      <MarketPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        initialMarketIds={markets.map((m) => m.id)}
        initialPrimaryId={primary?.id || null}
        suggestCity={suggestCity}
        suggestState={suggestState}
        onSave={handleSave}
        allowRequestUnlisted={variant === 'talent'}
      />
    </Card>
  );
}
